import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Heading,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  IconButton,
  Tooltip,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Input,
  InputGroup,
  InputRightElement,
  Progress,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Textarea,
} from '@chakra-ui/react';
import {
  DollarSign,
  Users,
  TrendingUp,
  Copy,
  Check,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  UserPlus,
  Link as LinkIcon,
  RefreshCw,
  ChevronRight,
  Trophy,
  Percent,
  Layers,
  Key,
  Plus,
  Trash2,
  Video,
  Mic,
  ToggleLeft,
  ToggleRight,
  Send,
  Wallet,
  Receipt,
  AlertTriangle,
} from 'lucide-react';
import { auth } from '../services/firebase';
import referralService, { AgentDashboardData } from '../services/referralService';
import transactionService from '../services/transactionService';
import gameApiService, { HostTournamentRow, HostQuota, HostPermissions, CreatorGroupInfo } from '../services/gameApiService';
import HostTournamentModal from '../components/tournament/HostTournamentModal';
import { RecordingsPanel } from './MyRecordingsPage';
import { Video as RecordingsIcon } from 'lucide-react';
import { POKER_COLORS } from '../theme/designSystem';
import PokerCard from '../components/ui/PokerCard';

//  NEW: Import bypass code service

import bypassCodeService, { BypassCode, BypassCodeUsageLog } from '../services/bypassCodeService';

//  NEW: Import tax service for PSS Coins tax tracking

import taxService, { AgentTaxSummary } from '../services/taxService';

// Note: Tutorial is now handled at App.tsx level to persist across page navigation

// Extended agent dashboard data with tournament rake
interface ExtendedAgentDashboardData extends AgentDashboardData {
  earnings: AgentDashboardData['earnings'] & {
    cashGameEarned?: number;
    tournamentEarned?: number;
    handsRaked?: number;
    tournamentsRaked?: number;
  };
}

const AgentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState<ExtendedAgentDashboardData | null>(null);
  const [copied, setCopied] = useState<'code' | 'link' | 'bypass' | null>(null);
  const toast = useToast();

  //  NEW: Bypass code state

  const [bypassCodes, setBypassCodes] = useState<BypassCode[]>([]);
  const [bypassCodeUsageLogs, setBypassCodeUsageLogs] = useState<BypassCodeUsageLog[]>([]);
  const [creatingBypassCode, setCreatingBypassCode] = useState(false);
  const [bypassCodeDescription, setBypassCodeDescription] = useState('');
  const [bypassQuota, setBypassQuota] = useState<any>(null);
  useEffect(() => { gameApiService.bypassCodeQuota().then(setBypassQuota).catch(() => {}); }, [bypassCodes.length]);
  const { isOpen: isBypassModalOpen, onOpen: onBypassModalOpen, onClose: onBypassModalClose } = useDisclosure();
  const [copiedBypassCodeId, setCopiedBypassCodeId] = useState<string | null>(null);

  //  Payout state

  const { isOpen: isPayoutOpen, onOpen: onPayoutOpen, onClose: onPayoutClose } = useDisclosure();
  // Creator tools: hosted tournaments, quota, permissions and the creator group
  const [hostInfo, setHostInfo] = useState<{ tournaments: HostTournamentRow[]; creatorGroup: CreatorGroupInfo | null; quota: HostQuota; permissions: HostPermissions } | null>(null);
  const { isOpen: isHostOpen, onOpen: onHostOpen, onClose: onHostClose } = useDisclosure();
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const loadHostInfo = async () => {
    const r = await gameApiService.hostTournaments();
    if (r.success) setHostInfo({ tournaments: r.tournaments, creatorGroup: r.creatorGroup, quota: r.quota, permissions: r.permissions });
  };
  const cancelHosted = async (t: HostTournamentRow) => {
    if (!window.confirm(`Cancel ${t.name}? Everyone registered is refunded.`)) return;
    setCancellingId(t.id);
    const r = await gameApiService.cancelTournament(t.id);
    setCancellingId(null);
    if (!r.success) { toast({ title: 'Could not cancel', description: r.error, status: 'error', duration: 5000 }); return; }
    toast({ title: 'Tournament cancelled', status: 'success', duration: 3000 });
    loadHostInfo();
  };
  // Manager view: the agents under this manager and the override earned on them
  const [managerTeam, setManagerTeam] = useState<{ agents: any[]; overrideRakePercent: number; totalOverrideEarned: number } | null>(null);
  const [payoutStatus, setPayoutStatus] = useState<{
    canRequestPayout: boolean;
    availableAmount: number;
    lastPayoutDate: Date | null;
    nextPayoutDate: Date;
    message?: string;
  } | null>(null);
  const [payoutAmount, setPayoutAmount] = useState('');
  // Where agent payouts go: the same PayPal / USDC destination saved in the
  // Wallet for redemptions. Loaded when the payout modal opens.
  const [payoutDests, setPayoutDests] = useState<Record<string, { value: string; network?: string | null; holdUntil?: string | null }>>({});
  const [usdcPayoutsAvailable, setUsdcPayoutsAvailable] = useState(false);
  const [agentPayoutMethod, setAgentPayoutMethod] = useState<'usdc' | 'paypal'>('paypal');
  useEffect(() => {
    if (!isPayoutOpen) return;
    gameApiService.payoutDestinations().then((r: any) => {
      if (!r?.success) return;
      const dests = r.destinations || {};
      setPayoutDests(dests);
      setUsdcPayoutsAvailable(!!r.usdcPayoutsEnabled);
      if (dests.paypal?.value) setAgentPayoutMethod('paypal');
      else if (dests.usdc?.value && r.usdcPayoutsEnabled) setAgentPayoutMethod('usdc');
    }).catch(() => {});
  }, [isPayoutOpen]);
  const [requestingPayout, setRequestingPayout] = useState(false);

  //  NEW: Tax tracking state

  const [taxData, setTaxData] = useState<AgentTaxSummary | null>(null);
  const [loadingTax, setLoadingTax] = useState(false);
  const currentYear = new Date().getFullYear();

  // Load dashboard data
  const loadDashboardData = async (showRefreshing = false) => {
    const user = auth.currentUser;
    if (!user) return;

    if (showRefreshing) setRefreshing(true);
    
    try {
      const data = await referralService.getAgentDashboardData(user.uid);
      setDashboardData(data as ExtendedAgentDashboardData);
      loadHostInfo().catch(() => {});
      if ((data as any)?.agent?.role === 'manager') {
        gameApiService.managerAgents().then(r => {
          if (r.success && r.isManager) setManagerTeam({ agents: r.agents, overrideRakePercent: r.overrideRakePercent ?? 0.05, totalOverrideEarned: r.totalOverrideEarned ?? 0 });
        }).catch(() => {});
      } else {
        setManagerTeam(null);
      }

      //  NEW: Load bypass codes for this agent

      const codes = await bypassCodeService.getCodesByOwner(user.uid);
      setBypassCodes(codes);

      // Load usage logs
      const logs = await bypassCodeService.getUsageLogsByOwner(user.uid, 50);
      setBypassCodeUsageLogs(logs);

      //  Load payout status

      const status = await transactionService.getAgentPayoutStatus(user.uid);
      setPayoutStatus(status);

      //  NEW: Load tax data

      try {
        setLoadingTax(true);
        const tax = await taxService.getAgentTaxSummary(currentYear);
        setTaxData(tax);
      } catch (taxError) {
        console.warn('Could not load tax data:', taxError);
      } finally {
        setLoadingTax(false);
      }

    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast({
        title: 'Error loading dashboard',
        description: 'Please try again later',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Copy to clipboard handlers
  const copyReferralCode = () => {
    if (dashboardData?.agent.referralCode) {
      navigator.clipboard.writeText(dashboardData.agent.customCode || dashboardData.agent.referralCode);
      setCopied('code');
      setTimeout(() => setCopied(null), 2000);
      toast({
        title: 'Referral code copied!',
        status: 'success',
        duration: 2000,
      });
    }
  };

  const copyReferralLink = () => {
    const code = dashboardData?.agent.customCode || dashboardData?.agent.referralCode;
    if (code) {
      const link = `${window.location.origin}/login?ref=${code}`;
      navigator.clipboard.writeText(link);
      setCopied('link');
      setTimeout(() => setCopied(null), 2000);
      toast({
        title: 'Referral link copied!',
        status: 'success',
        duration: 2000,
      });
    }
  };

  //  Handle payout request

  const handleRequestPayout = async () => {
    const user = auth.currentUser;
    if (!user || !payoutAmount) return;

    const amount = parseFloat(payoutAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({ title: 'Invalid amount', status: 'error', duration: 2000 });
      return;
    }

    if (!payoutDests[agentPayoutMethod]?.value) {
      toast({ title: 'Add a payout destination first', description: 'Go to Wallet > Redeem and save your PayPal contact or USDC wallet. Agent payouts go to the same place.', status: 'warning', duration: 6000 });
      return;
    }

    setRequestingPayout(true);
    try {
      const r = await transactionService.createAgentPayoutRequest(user.uid, amount, agentPayoutMethod);
      if (!r.success) throw new Error(r.error || 'Please try again');
      toast({
        title: 'Payout Requested!',

        description: `Your request for $${amount.toFixed(2)} by ${agentPayoutMethod === 'usdc' ? 'USDC' : 'PayPal'} is pending admin review.`,
        status: 'success',
        duration: 5000,
      });
      setPayoutAmount('');
      onPayoutClose();
      loadDashboardData(true);
    } catch (error: any) {
      toast({
        title: 'Payout Failed',
        description: error.message || 'Please try again',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setRequestingPayout(false);
    }
  };

  //  NEW: Copy bypass code

  const copyBypassCode = (code: string, codeId: string) => {
    navigator.clipboard.writeText(code);
    setCopiedBypassCodeId(codeId);
    setTimeout(() => setCopiedBypassCodeId(null), 2000);
    toast({
      title: 'Bypass code copied!',

      description: `Code: ${code}`,
      status: 'success',
      duration: 2000,
    });
  };

  //  NEW: Create bypass code

  const handleCreateBypassCode = async () => {
    const user = auth.currentUser;
    if (!user || !dashboardData) return;

    setCreatingBypassCode(true);
    try {
      const newCode = await bypassCodeService.createAgentBypassCode(
        user.uid,
        dashboardData.agent.displayName || 'Agent',
        bypassCodeDescription || undefined
      );

      toast({
        title: 'Bypass Code Created!',

        description: `Your new code: ${newCode.code}`,
        status: 'success',
        duration: 5000,
      });

      setBypassCodeDescription('');
      onBypassModalClose();
      
      // Refresh codes
      const codes = await bypassCodeService.getCodesByOwner(user.uid);
      setBypassCodes(codes);

    } catch (error: any) {
      console.error('Error creating bypass code:', error);
      toast({
        title: 'Error creating code',
        description: error.message || 'Unknown error',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setCreatingBypassCode(false);
    }
  };

  //  NEW: Toggle bypass code active status

  const handleToggleBypassCode = async (codeId: string, currentlyActive: boolean) => {
    try {
      if (currentlyActive) {
        await bypassCodeService.deactivateCode(codeId);
        toast({ title: 'Code deactivated', status: 'info', duration: 2000 });
      } else {
        await bypassCodeService.reactivateCode(codeId);
        toast({ title: 'Code reactivated', status: 'success', duration: 2000 });
      }
      
      // Refresh codes
      const user = auth.currentUser;
      if (user) {
        const codes = await bypassCodeService.getCodesByOwner(user.uid);
        setBypassCodes(codes);
      }
    } catch (error) {
      toast({ title: 'Error updating code', status: 'error', duration: 3000 });
    }
  };

  //  NEW: Delete bypass code

  const handleDeleteBypassCode = async (codeId: string) => {
    if (!window.confirm('Delete this bypass code permanently?')) return;

    try {
      await bypassCodeService.deleteCode(codeId);
      toast({ title: 'Code deleted', status: 'info', duration: 2000 });
      
      // Refresh codes
      const user = auth.currentUser;
      if (user) {
        const codes = await bypassCodeService.getCodesByOwner(user.uid);
        setBypassCodes(codes);
      }
    } catch (error) {
      toast({ title: 'Error deleting code', status: 'error', duration: 3000 });
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return `$${amount.toFixed(2)}`;
  };

  // Format date
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Calculate percentage change
  const getPercentChange = () => {
    if (!dashboardData) return 0;
    const { thisMonth, lastMonth } = dashboardData.earnings;
    if (lastMonth === 0) return thisMonth > 0 ? 100 : 0;
    return ((thisMonth - lastMonth) / lastMonth) * 100;
  };

  if (loading) {
    return (
      <Box
        w="100%"
        minH="100vh"
        bg="#0D1B2A"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack spacing={4}>
          <Spinner size="xl" color={POKER_COLORS.cyan} thickness="4px" />
          <Text color="gray.400">Loading your dashboard...</Text>
        </VStack>
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box
        w="100%"
        minH="100vh"
        bg="#0D1B2A"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack spacing={4}>
          <Text color="red.400" fontSize="xl">Failed to load dashboard data</Text>
          <Text
            color={POKER_COLORS.cyan}
            cursor="pointer"
            onClick={() => loadDashboardData()}
            _hover={{ textDecoration: 'underline' }}
          >
            Try again
          </Text>
        </VStack>
      </Box>
    );
  }

  //  FIX: Default to empty arrays to prevent .map() errors if Firestore returns null/undefined

  const { agent, earnings, payoutHistory } = dashboardData;
  //  FIX: referrals is an object with direct/indirect arrays, not a flat array

  const directReferrals = dashboardData.referrals?.direct || [];
  const indirectReferrals = dashboardData.referrals?.indirect || [];
  
  //  FIX: Deduplicate by referredUserId to prevent duplicate rows in UI

  // Note: Structure is ReferralWithDetails { referral: Referral, username, ... }
  const allReferralsRaw = [...directReferrals, ...indirectReferrals];
  const seenUserIds = new Set<string>();
  const allReferrals = allReferralsRaw.filter(item => {
    const odid = item.referral?.referredUserId;
    if (!odid || seenUserIds.has(odid)) {
      return false; // Skip duplicate or invalid
    }
    seenUserIds.add(odid);
    return true;
  });
  
  const safePayoutHistory = Array.isArray(payoutHistory) ? payoutHistory : [];
  const percentChange = getPercentChange();
  const directCount = allReferrals.filter(r => r.isDirect).length;
  const indirectCount = allReferrals.length - directCount;
  const activeCount = allReferrals.filter(r => r.active).length;
  const playersRakeGenerated = allReferrals.reduce((n, r) => n + (r.rakeGenerated || 0), 0);

  // Calculate cash game vs tournament split
  const cashGameEarned = earnings.cashGameEarned || 0;
  const tournamentEarned = earnings.tournamentEarned || 0;
  const totalEarned = earnings.totalEarned || (cashGameEarned + tournamentEarned);
  const cashGamePercent = totalEarned > 0 ? (cashGameEarned / totalEarned) * 100 : 50;
  const tournamentPercent = totalEarned > 0 ? (tournamentEarned / totalEarned) * 100 : 50;

  //  NEW: Calculate bypass code stats

  const activeBypassCodes = bypassCodes.filter(c => c.active).length;
  const totalBypassUsage = bypassCodes.reduce((sum, c) => sum + c.usageCount, 0);

  return (
    <Box w="100%" minH="100vh" bg="#0D1B2A" py={6} px={{ base: 4, md: 6 }}>
      <Container maxW="1400px">
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <HStack justify="space-between" align="center" wrap="wrap" gap={4}>
            <VStack align="start" spacing={1}>
              <HStack spacing={3}>
                <Heading size="lg" color="white">
                  Agent Dashboard
                </Heading>
                <Badge
                  colorScheme={agent.tier === 'vip' ? 'yellow' : agent.tier === 'premium' ? 'purple' : 'cyan'}
                  fontSize="sm"
                  px={3}
                  py={1}
                  borderRadius="full"
                  textTransform="uppercase"
                >
                  {agent.tier}
                </Badge>
              </HStack>
              <Text color="gray.400" fontSize="sm">
                Welcome back, {agent.displayName}! • {agent.rakePercent * 100}% rake share
              </Text>
            </VStack>

            <IconButton
              aria-label="Refresh"
              icon={<RefreshCw size={18} />}
              onClick={() => loadDashboardData(true)}
              isLoading={refreshing}
              variant="outline"
              colorScheme="cyan"
              size="sm"
            />
          </HStack>

          {/* Referral Code Section */}
          <Box
            bg="linear-gradient(135deg, rgba(0,217,217,0.15) 0%, rgba(0,136,170,0.1) 100%)"
            borderRadius="xl"
            border="1px solid"
            borderColor="cyan.800"
            p={4}
            data-tutorial="referral-link"
          >
            <HStack justify="space-between" wrap="wrap" gap={4}>
              <VStack align="start" spacing={2}>
                <Text color="gray.400" fontSize="sm" fontWeight="medium">
                  Your Referral Code
                </Text>
                <HStack spacing={3}>
                  <Text color={POKER_COLORS.gold} fontSize="2xl" fontWeight="bold" fontFamily="mono">
                    {agent.customCode || agent.referralCode}
                  </Text>
                  <Tooltip label={copied === 'code' ? 'Copied!' : 'Copy code'}>
                    <IconButton
                      aria-label="Copy code"
                      icon={copied === 'code' ? <Check size={16} /> : <Copy size={16} />}
                      onClick={copyReferralCode}
                      size="sm"
                      colorScheme={copied === 'code' ? 'green' : 'cyan'}
                      variant="ghost"
                    />
                  </Tooltip>
                </HStack>
              </VStack>

              <VStack align="start" spacing={2}>
                <Text color="gray.400" fontSize="sm" fontWeight="medium">
                  Share Link
                </Text>
                <InputGroup size="sm" maxW="350px">
                  <Input
                    value={`${window.location.origin}/login?ref=${agent.customCode || agent.referralCode}`}
                    isReadOnly
                    color="gray.300"
                    bg="blackAlpha.400"
                    border="1px solid"
                    borderColor="gray.700"
                    fontSize="xs"
                    fontFamily="mono"
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label="Copy link"
                      icon={copied === 'link' ? <Check size={14} /> : <LinkIcon size={14} />}
                      onClick={copyReferralLink}
                      size="xs"
                      colorScheme={copied === 'link' ? 'green' : 'cyan'}
                      variant="ghost"
                    />
                  </InputRightElement>
                </InputGroup>
              </VStack>
            </HStack>
          </Box>

          {/*  NEW: Voice/Video Bypass Codes Section */}

          <Box
            bg="linear-gradient(135deg, rgba(159,122,234,0.15) 0%, rgba(128,90,213,0.1) 100%)"
            borderRadius="xl"
            border="1px solid"
            data-tutorial="agent-codes"
            borderColor="purple.700"
            p={4}
          >
            <HStack justify="space-between" wrap="wrap" gap={4} mb={4}>
              <HStack spacing={3}>
                <Box p={2} bg="purple.900" borderRadius="lg">
                  <Key size={20} color="#9F7AEA" />
                </Box>
                <VStack align="start" spacing={0}>
                  <Text color="purple.300" fontWeight="bold" fontSize="lg">
                     Voice/Video Bypass Codes

                  </Text>
                  <Text color="gray.400" fontSize="sm">
                    Share these codes with your players: tables opened with a code get free voice/video and free table recording. Agent codes: {bypassQuota?.limit ?? 10} a month, each good for {bypassQuota?.validDays ?? 30} days and {bypassQuota?.maxUses ?? 100} tables.
                  </Text>
                </VStack>
              </HStack>
              <HStack spacing={3}>
                <VStack align="end" spacing={0}>
                  <Text color="purple.300" fontWeight="bold">{activeBypassCodes} Active</Text>
                  <Text color="gray.500" fontSize="xs">{totalBypassUsage} total uses</Text>
                  {bypassQuota && !bypassQuota.unlimited && (
                    <Text color={bypassQuota.remaining > 0 ? 'gray.400' : 'orange.300'} fontSize="xs">
                      {bypassQuota.remaining} of {bypassQuota.limit} left this month
                    </Text>
                  )}
                </VStack>
                <Button
                  leftIcon={<Plus size={16} />}
                  colorScheme="purple"
                  size="sm"
                  onClick={onBypassModalOpen}
                  isDisabled={!!bypassQuota && !bypassQuota.unlimited && bypassQuota.remaining <= 0}
                >
                  Generate Code
                </Button>
              </HStack>
            </HStack>

            {bypassCodes.length === 0 ? (
              <Box
                bg="blackAlpha.300"
                borderRadius="lg"
                p={6}
                textAlign="center"
              >
                <VStack spacing={3}>
                  <HStack spacing={2}>
                    <Mic size={24} color="#9F7AEA" />
                    <Video size={24} color="#9F7AEA" />
                  </HStack>
                  <Text color="gray.400">No bypass codes yet</Text>
                  <Text color="gray.500" fontSize="sm">
                    Generate a code to give your players free voice/video chat when creating tables
                  </Text>
                  <Button
                    leftIcon={<Plus size={16} />}
                    colorScheme="purple"
                    size="sm"
                    onClick={onBypassModalOpen}
                  >
                    Create Your First Code
                  </Button>
                </VStack>
              </Box>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                {bypassCodes.map(code => (
                  <Box
                    key={code.id}
                    bg="blackAlpha.300"
                    borderRadius="lg"
                    p={4}
                    border="1px solid"
                    borderColor={code.active ? 'purple.600' : 'gray.700'}
                    opacity={code.active ? 1 : 0.6}
                  >
                    <HStack justify="space-between" mb={2}>
                      <HStack spacing={2}>
                        <Text
                          color="purple.300"
                          fontWeight="bold"
                          fontFamily="mono"
                          fontSize="lg"
                          letterSpacing="0.1em"
                        >
                          {code.code}
                        </Text>
                        <Tooltip label={copiedBypassCodeId === code.id ? 'Copied!' : 'Copy code'}>
                          <IconButton
                            aria-label="Copy code"
                            icon={copiedBypassCodeId === code.id ? <Check size={14} /> : <Copy size={14} />}
                            onClick={() => copyBypassCode(code.code, code.id)}
                            size="xs"
                            colorScheme={copiedBypassCodeId === code.id ? 'green' : 'purple'}
                            variant="ghost"
                          />
                        </Tooltip>
                      </HStack>
                      <Badge colorScheme={code.active ? 'green' : 'red'} fontSize="xs">
                        {code.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </HStack>

                    <Text color="gray.500" fontSize="xs" mb={2}>
                      {code.description || 'No description'}
                    </Text>

                    <HStack justify="space-between" mt={3}>
                      <VStack align="start" spacing={0}>
                        <Text color={POKER_COLORS.cyan} fontSize="sm" fontWeight="bold">
                          {code.usageCount} uses
                        </Text>
                        <Text color="gray.600" fontSize="xs">
                          {code.lastUsedAt ? `Last: ${formatDate(code.lastUsedAt)}` : 'Never used'}
                        </Text>
                      </VStack>
                      <HStack spacing={1}>
                        <Tooltip label={code.active ? 'Deactivate' : 'Reactivate'}>
                          <IconButton
                            aria-label="Toggle code"
                            icon={code.active ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                            size="xs"
                            colorScheme={code.active ? 'green' : 'gray'}
                            variant="ghost"
                            onClick={() => handleToggleBypassCode(code.id, code.active)}
                          />
                        </Tooltip>
                        <Tooltip label="Delete code">
                          <IconButton
                            aria-label="Delete code"
                            icon={<Trash2 size={14} />}
                            size="xs"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => handleDeleteBypassCode(code.id)}
                          />
                        </Tooltip>
                      </HStack>
                    </HStack>
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </Box>

          {/* Manager: your team */}
          {managerTeam && (
            <Box bg="linear-gradient(145deg, rgba(20,30,48,0.9) 0%, rgba(10,15,25,0.95) 100%)" borderRadius="xl" border="1px solid" borderColor="orange.700" p={5}>
              <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
                <HStack><Users size={18} color="#fb923c" /><Text color="white" fontWeight="bold" fontSize="lg">Your Team</Text></HStack>
                <Text color="gray.400" fontSize="sm">
                  You earn {Math.round(managerTeam.overrideRakePercent * 100)}% of the rake from every player under your agents, on top of what they earn.
                </Text>
              </HStack>
              <HStack spacing={6} mb={4} flexWrap="wrap">
                <Box><Text color="gray.500" fontSize="xs">Override earned</Text><Text color={POKER_COLORS.gold} fontWeight="bold" fontSize="xl">${managerTeam.totalOverrideEarned.toFixed(2)}</Text></Box>
                <Box><Text color="gray.500" fontSize="xs">Agents</Text><Text color="white" fontWeight="bold" fontSize="xl">{managerTeam.agents.length}</Text></Box>
                <Box><Text color="gray.500" fontSize="xs">Players under your agents</Text><Text color="white" fontWeight="bold" fontSize="xl">{managerTeam.agents.reduce((n, a) => n + (a.directReferrals || 0) + (a.indirectReferrals || 0), 0)}</Text></Box>
              </HStack>
              {managerTeam.agents.length === 0 ? (
                <Text color="gray.500" fontSize="sm">No agents assigned to you yet.</Text>
              ) : (
                <Box overflowX="auto">
                  <Table size="sm" variant="unstyled">
                    <Thead><Tr borderBottom="1px solid" borderColor="whiteAlpha.100"><Th color="gray.500" fontSize="xs">Agent</Th><Th color="gray.500" fontSize="xs">Code</Th><Th color="gray.500" fontSize="xs">Tier</Th><Th color="gray.500" fontSize="xs">Players</Th><Th color="gray.500" fontSize="xs">Cash rake to them</Th><Th color="gray.500" fontSize="xs">Tournament rake to them</Th></Tr></Thead>
                    <Tbody>
                      {managerTeam.agents.map(a => (
                        <Tr key={a.id} borderBottom="1px solid" borderColor="whiteAlpha.50">
                          <Td color="white">{a.displayName}{!a.active && <Text as="span" color="red.300" fontSize="xs"> (inactive)</Text>}</Td>
                          <Td color={POKER_COLORS.cyan} fontFamily="mono">{a.code}</Td>
                          <Td color="gray.300">{a.tier}</Td>
                          <Td color="gray.300">{(a.directReferrals || 0) + (a.indirectReferrals || 0)}</Td>
                          <Td color={POKER_COLORS.gold}>${(a.totalRakeEarned || 0).toFixed(2)}</Td>
                          <Td color={POKER_COLORS.gold}>${(a.tournamentRakeEarned || 0).toFixed(2)}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </Box>
          )}

          {/* 🎬 Creator tools: your crew and your tournaments */}
          {hostInfo && (hostInfo.creatorGroup || hostInfo.permissions.canHost) && (
            <Box bg="linear-gradient(145deg, rgba(20,30,48,0.9) 0%, rgba(10,15,25,0.95) 100%)" borderRadius="xl" border="1px solid" borderColor={`${POKER_COLORS.gold}66`} p={5}>
              <HStack justify="space-between" mb={4} flexWrap="wrap" gap={3}>
                <HStack><Trophy size={18} color="#FFD700" /><Text color="white" fontWeight="bold" fontSize="lg">Creator tools</Text></HStack>
                {hostInfo.permissions.canHost && (
                  <HStack spacing={3} flexWrap="wrap">
                    <Text color="gray.400" fontSize="sm">
                      {hostInfo.permissions.isAdmin ? 'Admin: no monthly limit'
                        : `${Math.max(0, hostInfo.quota.freePerMonth - hostInfo.quota.usedThisMonth)} of ${hostInfo.quota.freePerMonth} free this month${hostInfo.quota.nextNeedsApproval ? ' · next one needs approval' : ''}`}
                    </Text>
                    <Button size="sm" bg="#FFD700" color="#06141F" _hover={{ bg: '#e6c200' }} leftIcon={<Trophy size={14} />} onClick={onHostOpen}>Host a tournament</Button>
                  </HStack>
                )}
              </HStack>

              <SimpleGrid columns={{ base: 1, md: hostInfo.permissions.canHost ? 2 : 1 }} spacing={4}>
                {hostInfo.creatorGroup && (
                  <Box p={4} bg="whiteAlpha.50" borderRadius="lg" border="1px solid" borderColor="whiteAlpha.100">
                    <HStack justify="space-between" mb={1}>
                      <HStack><Users size={16} color="#a78bfa" /><Text color="white" fontWeight="bold">{hostInfo.creatorGroup.name}</Text></HStack>
                      <Badge colorScheme="purple">Your crew</Badge>
                    </HStack>
                    <Text color="gray.400" fontSize="sm" mb={3}>
                      {hostInfo.creatorGroup.memberCount} member{hostInfo.creatorGroup.memberCount === 1 ? '' : 's'}. Everyone who signs up with your code lands here automatically. Rally them to a table or schedule a game from the group page.
                    </Text>
                    <HStack spacing={2} flexWrap="wrap">
                      <Button size="sm" colorScheme="purple" variant="outline" onClick={() => navigate(`/groups/${hostInfo.creatorGroup!.id}`)}>Open group</Button>
                      {hostInfo.creatorGroup.joinCode && (
                        <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard?.writeText(hostInfo.creatorGroup!.joinCode!); toast({ title: 'Join code copied', status: 'success', duration: 2000 }); }}>Copy join code {hostInfo.creatorGroup.joinCode}</Button>
                      )}
                    </HStack>
                  </Box>
                )}
                {hostInfo.permissions.canHost && (
                  <Box p={4} bg="whiteAlpha.50" borderRadius="lg" border="1px solid" borderColor="whiteAlpha.100">
                    <HStack justify="space-between" mb={2}>
                      <Text color="white" fontWeight="bold">Your tournaments</Text>
                      <Text color="gray.500" fontSize="xs">{hostInfo.permissions.mediaAllowed === 'none' ? 'Voice/video: off' : `Voice/video: ${hostInfo.permissions.mediaAllowed === 'video' ? 'voice + video' : 'voice'}`}</Text>
                    </HStack>
                    {hostInfo.tournaments.length === 0 ? (
                      <Text color="gray.500" fontSize="sm">Nothing yet. Your first two each month go live the moment you publish.</Text>
                    ) : (
                      <VStack align="stretch" spacing={2} maxH="260px" overflowY="auto">
                        {hostInfo.tournaments.slice(0, 8).map(t => {
                          const st = t.status === 'pending_approval' ? { label: 'Awaiting approval', c: 'yellow' }
                            : t.status === 'cancelled' ? { label: t.hostApproval === 'rejected' ? 'Not approved' : 'Cancelled', c: 'red' }
                            : t.status === 'completed' ? { label: 'Finished', c: 'gray' }
                            : ['running', 'active', 'in_progress', 'final_table', 'heads_up'].includes(t.status) ? { label: 'Live', c: 'green' }
                            : { label: 'Open for registration', c: 'cyan' };
                          const canCancel = ['registering', 'pending_approval', 'scheduled', 'upcoming'].includes(t.status);
                          return (
                            <Box key={t.id} p={3} bg="#06141F" borderRadius="md" border="1px solid" borderColor="whiteAlpha.100">
                              <HStack justify="space-between" align="start" flexWrap="wrap" gap={2}>
                                <Box minW={0}>
                                  <HStack spacing={2} flexWrap="wrap">
                                    <Text color="white" fontWeight="semibold" fontSize="sm">{t.name}</Text>
                                    <Badge colorScheme={st.c} fontSize="0.6rem">{st.label}</Badge>
                                    {t.media !== 'off' && <Badge colorScheme="green" fontSize="0.6rem">{t.media === 'video' ? `Video · ${t.mediaScope === 'final' ? 'final table' : 'all tables'}` : 'Voice'}</Badge>}
                                  </HStack>
                                  <Text color="gray.400" fontSize="xs">
                                    {t.startTime ? new Date(t.startTime).toLocaleString() : ''} · {t.currency === 'pkcash' ? `$${t.buyInAmount}` : t.buyInAmount === 0 ? 'Free' : `${t.buyInAmount.toLocaleString()} PC`} · {t.registered}/{t.maxPlayers} registered
                                    {t.hostRejectNote ? ` · ${t.hostRejectNote}` : ''}
                                  </Text>
                                </Box>
                                <HStack spacing={1}>
                                  {t.inviteCode && (
                                    <Button size="xs" variant="ghost" color={POKER_COLORS.cyan} onClick={() => { navigator.clipboard?.writeText(t.inviteCode!); toast({ title: `Code ${t.inviteCode} copied`, status: 'success', duration: 2000 }); }}>Code {t.inviteCode}</Button>
                                  )}
                                  {canCancel && (
                                    <Button size="xs" variant="ghost" colorScheme="red" isLoading={cancellingId === t.id} onClick={() => cancelHosted(t)}>Cancel</Button>
                                  )}
                                </HStack>
                              </HStack>
                            </Box>
                          );
                        })}
                      </VStack>
                    )}
                  </Box>
                )}
              </SimpleGrid>
              {hostInfo.permissions.canHost && (
                <HostTournamentModal isOpen={isHostOpen} onClose={onHostClose} permissions={hostInfo.permissions} quota={hostInfo.quota} creatorGroupName={hostInfo.creatorGroup?.name || null} onCreated={() => loadHostInfo()} />
              )}
            </Box>
          )}

          {/* Stats Cards */}
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} data-tutorial="agent-earnings">
            {/* Total Earnings */}
            <Box
              bg="linear-gradient(145deg, rgba(20,30,48,0.9) 0%, rgba(10,15,25,0.95) 100%)"
              borderRadius="xl"
              border="1px solid"
              borderColor="whiteAlpha.100"
              p={5}
            >
              <HStack justify="space-between" mb={3}>
                <Box p={2} bg="green.900" borderRadius="lg">
                  <DollarSign size={20} color="#22c55e" />
                </Box>
              </HStack>
              <Stat>
                <StatLabel color="gray.400">Total Earnings</StatLabel>
                <StatNumber color={POKER_COLORS.gold} fontSize="2xl">
                  {formatCurrency(totalEarned)}
                </StatNumber>
                <StatHelpText color="gray.500" mb={0}>
                  Lifetime rake commissions
                </StatHelpText>
              </Stat>
            </Box>

            {/* This Month */}
            <Box
              bg="linear-gradient(145deg, rgba(20,30,48,0.9) 0%, rgba(10,15,25,0.95) 100%)"
              borderRadius="xl"
              border="1px solid"
              borderColor="whiteAlpha.100"
              p={5}
            >
              <HStack justify="space-between" mb={3}>
                <Box p={2} bg="cyan.900" borderRadius="lg">
                  <TrendingUp size={20} color={POKER_COLORS.cyan} />
                </Box>
                {percentChange !== 0 && (
                  <Badge
                    colorScheme={percentChange > 0 ? 'green' : 'red'}
                    fontSize="xs"
                    px={2}
                    py={1}
                    borderRadius="full"
                  >
                    {percentChange > 0 ? '+' : ''}{percentChange.toFixed(0)}%
                  </Badge>
                )}
              </HStack>
              <Stat>
                <StatLabel color="gray.400">This Month</StatLabel>
                <StatNumber color="white" fontSize="2xl">
                  {formatCurrency(earnings.thisMonth)}
                </StatNumber>
                <StatHelpText color="gray.500" mb={0}>
                  vs {formatCurrency(earnings.lastMonth)} last month
                </StatHelpText>
              </Stat>
            </Box>

            {/* Total Players */}
            <Box
              bg="linear-gradient(145deg, rgba(20,30,48,0.9) 0%, rgba(10,15,25,0.95) 100%)"
              borderRadius="xl"
              border="1px solid"
              borderColor="whiteAlpha.100"
              p={5}
            >
              <HStack justify="space-between" mb={3}>
                <Box p={2} bg="purple.900" borderRadius="lg">
                  <Users size={20} color="#a855f7" />
                </Box>
              </HStack>
              <Stat>
                <StatLabel color="gray.400">Total Players</StatLabel>
                <StatNumber color="white" fontSize="2xl">
                  {allReferrals.length}
                </StatNumber>
                <StatHelpText color="gray.500" mb={0}>
                  {directCount} direct · {indirectCount} indirect · {activeCount} active this month
                </StatHelpText>
              </Stat>
            </Box>

            {/* Rake Share */}
            <Box
              bg="linear-gradient(145deg, rgba(20,30,48,0.9) 0%, rgba(10,15,25,0.95) 100%)"
              borderRadius="xl"
              border="1px solid"
              borderColor="whiteAlpha.100"
              p={5}
            >
              <HStack justify="space-between" mb={3}>
                <Box p={2} bg="orange.900" borderRadius="lg">
                  <Percent size={20} color="#f97316" />
                </Box>
              </HStack>
              <Stat>
                <StatLabel color="gray.400">Your Rake Share</StatLabel>
                <StatNumber color="white" fontSize="2xl">
                  {(agent.rakePercent * 100).toFixed(0)}%
                </StatNumber>
                <StatHelpText color="gray.500" mb={0}>
                  {agent.tier.charAt(0).toUpperCase() + agent.tier.slice(1)} tier
                </StatHelpText>
              </Stat>
            </Box>
          </SimpleGrid>

          {/*  Request Payout Section */}

          <Box
            bg="linear-gradient(145deg, rgba(20,30,48,0.9) 0%, rgba(10,15,25,0.95) 100%)"
            borderRadius="xl"
            border="1px solid"
            borderColor={payoutStatus?.canRequestPayout ? 'green.500' : 'whiteAlpha.100'}
            p={5}
            data-tutorial="agent-payout"
          >
            <HStack justify="space-between" align="center" flexWrap="wrap" gap={4}>
              <HStack spacing={4}>
                <Box p={3} bg="green.900" borderRadius="lg">
                  <Send size={24} color="#22c55e" />
                </Box>
                <VStack align="start" spacing={0}>
                  <Text color="white" fontWeight="bold" fontSize="lg">Request Payout</Text>
                  <Text color="gray.400" fontSize="sm">
                    Available: <Text as="span" color={POKER_COLORS.gold} fontWeight="bold">
                      {formatCurrency(payoutStatus?.availableAmount || 0)}
                    </Text>
                  </Text>
                  {payoutStatus?.message && (
                    <Text color="orange.300" fontSize="xs">{payoutStatus.message}</Text>
                  )}
                </VStack>
              </HStack>
              <Button
                colorScheme="green"
                leftIcon={<Wallet size={16} />}
                onClick={onPayoutOpen}
                isDisabled={!payoutStatus?.canRequestPayout}
                size="md"
              >
                {payoutStatus?.canRequestPayout ? 'Request Payout' : 'Unavailable'}
              </Button>
            </HStack>
          </Box>

          {/*  Earnings Breakdown Section */}

          <Box
            bg="linear-gradient(145deg, rgba(20,30,48,0.9) 0%, rgba(10,15,25,0.95) 100%)"
            borderRadius="xl"
            border="1px solid"
            borderColor="whiteAlpha.100"
            p={6}
          >
            <HStack mb={4}>
              <Layers size={20} color={POKER_COLORS.cyan} />
              <Text color="white" fontWeight="bold" fontSize="lg">Earnings Breakdown</Text>
            </HStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              {/* Cash Game Earnings */}
              <Box
                bg="blackAlpha.300"
                p={4}
                borderRadius="lg"
                border="1px solid"
                borderColor="whiteAlpha.100"
              >
                <HStack justify="space-between" mb={3}>
                  <HStack>
                    <Box p={2} bg={`${POKER_COLORS.cyan}20`} borderRadius="md">
                      <DollarSign size={16} color={POKER_COLORS.cyan} />
                    </Box>
                    <Text color="white" fontWeight="bold">Cash Games</Text>
                  </HStack>
                  <Badge bg={`${POKER_COLORS.cyan}20`} color={POKER_COLORS.cyan}>
                    {cashGamePercent.toFixed(0)}%
                  </Badge>
                </HStack>
                <Text color={POKER_COLORS.gold} fontSize="2xl" fontWeight="bold">
                  {formatCurrency(cashGameEarned)}
                </Text>
                <Text color="gray.500" fontSize="sm" mt={1}>
                  From {earnings.handsRaked || 0} hands raked
                </Text>
                <Progress
                  value={cashGamePercent}
                  size="xs"
                  colorScheme="cyan"
                  mt={3}
                  borderRadius="full"
                  bg="whiteAlpha.100"
                />
              </Box>

              {/* Tournament Earnings */}
              <Box
                bg="blackAlpha.300"
                p={4}
                borderRadius="lg"
                border="1px solid"
                borderColor="whiteAlpha.100"
              >
                <HStack justify="space-between" mb={3}>
                  <HStack>
                    <Box p={2} bg={`${POKER_COLORS.gold}20`} borderRadius="md">
                      <Trophy size={16} color={POKER_COLORS.gold} />
                    </Box>
                    <Text color="white" fontWeight="bold">Tournaments</Text>
                  </HStack>
                  <Badge bg={`${POKER_COLORS.gold}20`} color={POKER_COLORS.gold}>
                    {tournamentPercent.toFixed(0)}%
                  </Badge>
                </HStack>
                <Text color={POKER_COLORS.gold} fontSize="2xl" fontWeight="bold">
                  {formatCurrency(tournamentEarned)}
                </Text>
                <Text color="gray.500" fontSize="sm" mt={1}>
                  From {earnings.tournamentsRaked || 0} tournaments
                </Text>
                <Progress
                  value={tournamentPercent}
                  size="xs"
                  colorScheme="yellow"
                  mt={3}
                  borderRadius="full"
                  bg="whiteAlpha.100"
                />
              </Box>
            </SimpleGrid>

            {/* Earnings by Source Visual */}
            <Box mt={4}>
              <HStack justify="space-between" mb={2}>
                <Text color="gray.400" fontSize="sm">Earnings Distribution</Text>
                <Text color="gray.500" fontSize="xs">
                  Cash Games vs Tournaments
                </Text>
              </HStack>
              <Box h={3} bg="whiteAlpha.100" borderRadius="full" overflow="hidden">
                <HStack h="100%" spacing={0}>
                  <Box
                    h="100%"
                    w={`${cashGamePercent}%`}
                    bg={POKER_COLORS.cyan}
                    transition="width 0.3s"
                  />
                  <Box
                    h="100%"
                    w={`${tournamentPercent}%`}
                    bg={POKER_COLORS.gold}
                    transition="width 0.3s"
                  />
                </HStack>
              </Box>
              <HStack justify="space-between" mt={2}>
                <HStack spacing={2}>
                  <Box w={3} h={3} bg={POKER_COLORS.cyan} borderRadius="sm" />
                  <Text color="gray.500" fontSize="xs">Cash Games</Text>
                </HStack>
                <HStack spacing={2}>
                  <Box w={3} h={3} bg={POKER_COLORS.gold} borderRadius="sm" />
                  <Text color="gray.500" fontSize="xs">Tournaments</Text>
                </HStack>
              </HStack>
            </Box>
          </Box>

          {/* Tabs for Referrals, Payouts, and Bypass Code Usage */}
          <Tabs variant="unstyled" colorScheme="cyan" data-tutorial="referral-list">
            <TabList gap={2}>
              <Tab
                bg="whiteAlpha.100"
                color="gray.400"
                borderRadius="lg"
                px={4}
                py={2}
                _selected={{ bg: POKER_COLORS.cyan, color: 'white' }}
                _hover={{ bg: 'whiteAlpha.200' }}
                fontWeight="medium"
              >
                <HStack spacing={2}>
                  <Users size={16} />
                  <Text>Your Players ({allReferrals.length})</Text>
                </HStack>
              </Tab>
              <Tab
                bg="whiteAlpha.100"
                color="gray.400"
                borderRadius="lg"
                px={4}
                py={2}
                _selected={{ bg: POKER_COLORS.cyan, color: 'white' }}
                _hover={{ bg: 'whiteAlpha.200' }}
                fontWeight="medium"
              >
                <HStack spacing={2}>
                  <Calendar size={16} />
                  <Text>Payout History</Text>
                </HStack>
              </Tab>
              {/*  NEW: Bypass Code Usage Tab */}

              <Tab
                bg="whiteAlpha.100"
                color="gray.400"
                borderRadius="lg"
                px={4}
                py={2}
                _selected={{ bg: 'purple.500', color: 'white' }}
                _hover={{ bg: 'whiteAlpha.200' }}
                fontWeight="medium"
              >
                <HStack spacing={2}>
                  <Key size={16} />
                  <Text>Code Usage ({bypassCodeUsageLogs.length})</Text>
                </HStack>
              </Tab>
              {/*  NEW: Tax Information Tab */}

              <Tab
                data-tutorial="agent-tax"
                bg="whiteAlpha.100"
                color="gray.400"
                borderRadius="lg"
                px={4}
                py={2}
                _selected={{ bg: 'green.600', color: 'white' }}
                _hover={{ bg: 'whiteAlpha.200' }}
                fontWeight="medium"
              >
                <HStack spacing={2}>
                  <Receipt size={16} />
                  <Text>Tax Info</Text>
                  {taxData?.requires1099 && (
                    <Badge colorScheme="yellow" fontSize="xs">1099</Badge>
                  )}
                </HStack>
              </Tab>
              <Tab
                bg="whiteAlpha.100"
                color="gray.400"
                borderRadius="lg"
                px={4}
                py={2}
                _selected={{ bg: 'red.600', color: 'white' }}
                _hover={{ bg: 'whiteAlpha.200' }}
                fontWeight="medium"
              >
                <HStack spacing={2}>
                  <RecordingsIcon size={16} />
                  <Text>Recordings</Text>
                  <Badge colorScheme="green" fontSize="xs">Free</Badge>
                </HStack>
              </Tab>
            </TabList>

            <TabPanels>
              {/* Referrals Tab */}
              <TabPanel px={0}>
                <Box
                  bg="linear-gradient(145deg, rgba(20,30,48,0.9) 0%, rgba(10,15,25,0.95) 100%)"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="whiteAlpha.100"
                  overflow="hidden"
                >
                  {allReferrals.length === 0 ? (
                    <VStack py={12} spacing={4}>
                      <UserPlus size={48} color={POKER_COLORS.cyan} />
                      <Text color="gray.400" textAlign="center">
                        No players yet. Share your referral code to start earning!
                      </Text>
                    </VStack>
                  ) : (
                    <>
                      <HStack px={5} py={3} spacing={6} flexWrap="wrap" borderBottom="1px solid" borderColor="whiteAlpha.100">
                        <HStack spacing={2}><Badge colorScheme="cyan">Direct</Badge><Text color="gray.300" fontSize="sm">{directCount} signed up with your code</Text></HStack>
                        <HStack spacing={2}><Badge colorScheme="purple">Indirect</Badge><Text color="gray.300" fontSize="sm">{indirectCount} brought in by your players</Text></HStack>
                        <HStack spacing={2}><Badge colorScheme="green">Active</Badge><Text color="gray.300" fontSize="sm">{activeCount} played in the last 30 days</Text></HStack>
                        <Text color="gray.500" fontSize="sm" ml="auto">Rake your players generated: <Text as="span" color="white" fontWeight="bold">{formatCurrency(playersRakeGenerated)}</Text></Text>
                      </HStack>
                      <Box overflowX="auto">
                        <Table variant="unstyled">
                          <Thead>
                            <Tr borderBottom="1px solid" borderColor="whiteAlpha.100">
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4}>Player</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4}>Joined</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4}>Last played</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4} isNumeric>Hands</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4} isNumeric>Tourneys</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4} isNumeric>Rake generated</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4} isNumeric>Your earnings</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {allReferrals.map((r, index) => (
                              <Tr key={r.referral?.id || `ref-${index}`} borderBottom="1px solid" borderColor="whiteAlpha.50" _hover={{ bg: 'whiteAlpha.50' }}>
                                <Td py={3}>
                                  <HStack spacing={3}>
                                    <Box w={8} h={8} borderRadius="full" overflow="hidden" flexShrink={0} bg={`hsl(${index * 40}, 60%, 50%)`} display="flex" alignItems="center" justifyContent="center" color="white" fontWeight="bold" fontSize="sm">
                                      {r.photoURL ? <img src={r.photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (r.username || '?').charAt(0).toUpperCase()}
                                    </Box>
                                    <VStack align="start" spacing={0}>
                                      <HStack spacing={2}>
                                        <Text color="white" fontWeight="medium" fontSize="sm">{r.username || 'Anonymous'}</Text>
                                        <Badge colorScheme={r.isDirect ? 'cyan' : 'purple'} fontSize="0.6rem">{r.isDirect ? 'Direct' : 'Indirect'}</Badge>
                                        {r.active && <Badge colorScheme="green" fontSize="0.6rem">Active</Badge>}
                                      </HStack>
                                      {!r.isDirect && r.referredVia && (
                                        <Text color="purple.300" fontSize="xs">via {r.referredVia}</Text>
                                      )}
                                    </VStack>
                                  </HStack>
                                </Td>
                                <Td py={3}><Text color="gray.400" fontSize="sm">{r.signupDate.getTime() > 0 ? formatDate(r.signupDate) : '—'}</Text></Td>
                                <Td py={3}><Text color={r.active ? 'green.300' : 'gray.500'} fontSize="sm">{r.lastPlayedAt ? formatDate(r.lastPlayedAt) : 'Not yet'}</Text></Td>
                                <Td py={3} isNumeric><Text color="gray.300" fontSize="sm">{r.handsPlayed}</Text></Td>
                                <Td py={3} isNumeric><Text color="gray.300" fontSize="sm">{r.tournamentsPlayed}</Text></Td>
                                <Td py={3} isNumeric>
                                  <Text color="white" fontSize="sm" fontWeight="medium">{r.generatedEstimated ? '~' : ''}{formatCurrency(r.rakeGenerated)}</Text>
                                  {(r.cashRakeGenerated > 0 || r.tournamentRakeGenerated > 0) && (
                                    <Text color="gray.500" fontSize="xs">cash {formatCurrency(r.cashRakeGenerated)} · tourney {formatCurrency(r.tournamentRakeGenerated)}</Text>
                                  )}
                                </Td>
                                <Td py={3} isNumeric><Text color={POKER_COLORS.gold} fontSize="sm" fontWeight="bold">{formatCurrency(r.rakeEarned)}</Text></Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                      <Text px={5} py={3} color="gray.500" fontSize="xs">
                        Rake generated is what the house took on that player's hands and tournament entries. Your earnings are your {(agent.rakePercent * 100).toFixed(0)}% share of it, cash games and tournaments. A ~ marks older records where the generated amount is worked back from your share.
                      </Text>
                    </>
                  )}
                </Box>
              </TabPanel>

              {/* Payout History Tab */}
              <TabPanel px={0}>
                <Box
                  bg="linear-gradient(145deg, rgba(20,30,48,0.9) 0%, rgba(10,15,25,0.95) 100%)"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="whiteAlpha.100"
                  overflow="hidden"
                >
                  {safePayoutHistory.length === 0 ? (
                    <VStack py={12} spacing={4}>
                      <Calendar size={48} color={POKER_COLORS.cyan} />
                      <Text color="gray.400" textAlign="center">
                        No payouts yet. Earnings will appear as your players generate rake.
                      </Text>
                    </VStack>
                  ) : (
                    <Box overflowX="auto">
                      <Table variant="unstyled">
                        <Thead>
                          <Tr borderBottom="1px solid" borderColor="whiteAlpha.100">
                            <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4}>
                              Date
                            </Th>
                            <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4}>
                              Source
                            </Th>
                            <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4}>
                              Description
                            </Th>
                            <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4} isNumeric>
                              Amount
                            </Th>
                            <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4}>
                              Status
                            </Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {safePayoutHistory.map((payout) => {
                            const method = (payout as any).method as string | undefined;
                            const st = String(payout.status || '');
                            const stLabel = st === 'pending_review' ? 'pending review' : st === 'paid' ? 'paid' : st === 'approved' ? 'approved' : st === 'processing' ? 'sending' : st === 'rejected' ? 'returned' : st || 'pending';
                            return (
                              <Tr
                                key={payout.id}
                                borderBottom="1px solid"
                                borderColor="whiteAlpha.50"
                                _hover={{ bg: 'whiteAlpha.50' }}
                              >
                                <Td py={4}>
                                  <Text color="gray.400" fontSize="sm">
                                    {formatDate(payout.date)}
                                  </Text>
                                </Td>
                                <Td py={4}>
                                  <Badge
                                    colorScheme={method === 'usdc' ? 'purple' : 'cyan'}
                                    fontSize="xs"
                                  >
                                    {method === 'usdc' ? 'USDC' : method === 'paypal' ? 'PayPal' : 'Payout'}
                                  </Badge>
                                </Td>
                                <Td py={4}>
                                  <Text color="gray.400" fontSize="sm">
                                    {payout.description || 'Payout request'}{(payout as any).reference ? ` - ${(payout as any).reference}` : ''}
                                  </Text>
                                </Td>
                                <Td py={4} isNumeric>
                                  <Text color={POKER_COLORS.gold} fontWeight="bold">
                                    +{formatCurrency(payout.amount)}
                                  </Text>
                                </Td>
                                <Td py={4}>
                                  <Badge
                                    colorScheme={st === 'paid' || st === 'completed' ? 'green' : st === 'rejected' ? 'red' : 'orange'}
                                    fontSize="xs"
                                  >
                                    {stLabel}
                                  </Badge>
                                </Td>
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>
                    </Box>
                  )}
                </Box>
              </TabPanel>

              {/*  NEW: Bypass Code Usage Tab */}

              <TabPanel px={0}>
                <Box
                  bg="linear-gradient(145deg, rgba(20,30,48,0.9) 0%, rgba(10,15,25,0.95) 100%)"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="whiteAlpha.100"
                  overflow="hidden"
                >
                  {bypassCodeUsageLogs.length === 0 ? (
                    <VStack py={12} spacing={4}>
                      <Key size={48} color="#9F7AEA" />
                      <Text color="gray.400" textAlign="center">
                        No bypass code usage yet
                      </Text>
                      <Text color="gray.500" fontSize="sm" textAlign="center">
                        When players use your codes to create tables with voice/video, usage will appear here
                      </Text>
                    </VStack>
                  ) : (
                    <Box overflowX="auto">
                      <Table variant="unstyled">
                        <Thead>
                          <Tr borderBottom="1px solid" borderColor="whiteAlpha.100">
                            <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4}>
                              Code
                            </Th>
                            <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4}>
                              Used By
                            </Th>
                            <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4}>
                              Table
                            </Th>
                            <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4}>
                              Date
                            </Th>
                            <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4} isNumeric>
                              Fee Waived
                            </Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {bypassCodeUsageLogs.map((log) => (
                            <Tr
                              key={log.id}
                              borderBottom="1px solid"
                              borderColor="whiteAlpha.50"
                              _hover={{ bg: 'whiteAlpha.50' }}
                            >
                              <Td py={4}>
                                <Text color="purple.300" fontFamily="mono" fontWeight="bold">
                                  {log.code}
                                </Text>
                              </Td>
                              <Td py={4}>
                                <Text color="white" fontSize="sm">
                                  {log.usedByName || log.usedBy?.substring(0, 8)}
                                </Text>
                              </Td>
                              <Td py={4}>
                                <Text color="gray.400" fontSize="sm">
                                  {log.tableName || log.tableId?.substring(0, 8)}
                                </Text>
                              </Td>
                              <Td py={4}>
                                <Text color="gray.400" fontSize="sm">
                                  {log.usedAt.toLocaleString()}
                                </Text>
                              </Td>
                              <Td py={4} isNumeric>
                                <Text color={POKER_COLORS.cyan} fontWeight="bold">
                                  $0.50
                                </Text>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  )}
                </Box>
              </TabPanel>

              {/*  NEW: Tax Information TabPanel */}

              <TabPanel px={0}>
                <Box
                  bg="linear-gradient(145deg, rgba(20,30,48,0.9) 0%, rgba(10,15,25,0.95) 100%)"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="green.800"
                  p={6}
                >
                  <VStack spacing={6} align="stretch">
                    {/* Header */}
                    <HStack justify="space-between">
                      <VStack align="start" spacing={1}>
                        <Heading size="md" color="white">
                          Tax Information ({currentYear})
                        </Heading>
                        <Text color="gray.400" fontSize="sm">
                          Rake earnings are reportable income (dollar equivalent)
                        </Text>
                      </VStack>
                      {taxData?.requires1099 && (
                        <Badge colorScheme="yellow" fontSize="md" px={3} py={1}>
                          <HStack spacing={1}>
                            <AlertTriangle size={14} />
                            <Text>1099 Required</Text>
                          </HStack>
                        </Badge>
                      )}
                    </HStack>

                    {loadingTax ? (
                      <VStack py={8}>
                        <Spinner color="green.400" size="lg" />
                        <Text color="gray.400">Loading tax data...</Text>
                      </VStack>
                    ) : (
                      <>
                        {/* Tax Summary Cards */}
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                          <PokerCard>
                            <Stat>
                              <StatLabel color="gray.400">Total Commissions ({currentYear})</StatLabel>
                              <StatNumber color="green.400" fontSize="2xl">
                                ${(taxData?.totalCommissions || 0).toFixed(2)}
                              </StatNumber>
                              <StatHelpText color="gray.500">
                                Earned from rake (cash games, tournaments, overrides)
                              </StatHelpText>
                            </Stat>
                          </PokerCard>

                          <PokerCard>
                            <Stat>
                              <StatLabel color="gray.400">1099 Threshold</StatLabel>
                              <StatNumber color={taxData?.requires1099 ? 'yellow.400' : 'gray.400'} fontSize="2xl">
                                ${taxData?.threshold1099 || 600}
                              </StatNumber>
                              <StatHelpText color="gray.500">
                                {taxData?.requires1099 
                                  ? `You're $${((taxData?.totalCommissions || 0) - (taxData?.threshold1099 || 600)).toFixed(2)} over`
                                  : `$${((taxData?.threshold1099 || 600) - (taxData?.totalCommissions || 0)).toFixed(2)} remaining`
                                }
                              </StatHelpText>
                            </Stat>
                          </PokerCard>

                          <PokerCard>
                            <Stat>
                              <StatLabel color="gray.400">Est. Tax Liability</StatLabel>
                              <StatNumber color="orange.400" fontSize="2xl">
                                ${(taxData?.estimatedTax || 0).toFixed(2)}
                              </StatNumber>
                              <StatHelpText color="gray.500">
                                ~{taxData?.taxBracket || '22%'} bracket (estimate)
                              </StatHelpText>
                            </Stat>
                          </PokerCard>
                        </SimpleGrid>

                        {/* Monthly Breakdown */}
                        {taxData?.monthlyCommissions && Object.keys(taxData.monthlyCommissions).some(k => taxData.monthlyCommissions[k] > 0) && (
                          <Box>
                            <Text color="white" fontWeight="bold" mb={3}>Monthly Breakdown</Text>
                            <Box
                              bg="whiteAlpha.50"
                              borderRadius="lg"
                              overflow="hidden"
                            >
                              <Table size="sm">
                                <Thead bg="whiteAlpha.100">
                                  <Tr>
                                    <Th color="gray.400">Month</Th>
                                    <Th color="gray.400" isNumeric>Commissions</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {Object.entries(taxData.monthlyCommissions)
                                    .filter(([_, amount]) => amount > 0)
                                    .sort((a, b) => a[0].localeCompare(b[0]))
                                    .map(([month, amount]) => (
                                      <Tr key={month}>
                                        <Td color="gray.300">
                                          {new Date(month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                        </Td>
                                        <Td isNumeric color="green.400" fontWeight="medium">
                                          ${amount.toFixed(2)}
                                        </Td>
                                      </Tr>
                                    ))}
                                </Tbody>
                              </Table>
                            </Box>
                          </Box>
                        )}

                        {/* Important Notice */}
                        <Box
                          bg="yellow.900"
                          border="1px solid"
                          borderColor="yellow.600"
                          borderRadius="lg"
                          p={4}
                        >
                          <HStack align="start" spacing={3}>
                            <AlertTriangle size={20} color="#ECC94B" style={{ marginTop: 2, flexShrink: 0 }} />
                            <VStack align="start" spacing={2}>
                              <Text color="yellow.200" fontWeight="bold">Important Tax Information</Text>
                              <Text color="yellow.100" fontSize="sm">
                                Rake earnings are paid in US dollars. Earnings over $600/year require a 1099-MISC form.
                                This is an estimate only - consult a tax professional for accurate tax advice.
                                Self-employment tax (15.3%) applies to commission income.
                              </Text>
                            </VStack>
                          </HStack>
                        </Box>

                        {/* 1099 Status */}
                        {taxData?.requires1099 && (
                          <Box
                            bg="green.900"
                            border="1px solid"
                            borderColor="green.600"
                            borderRadius="lg"
                            p={4}
                          >
                            <HStack justify="space-between" align="center">
                              <VStack align="start" spacing={1}>
                                <Text color="green.200" fontWeight="bold">1099-MISC Form</Text>
                                <Text color="green.100" fontSize="sm">
                                  You'll receive a 1099-MISC by January 31st of next year
                                </Text>
                              </VStack>
                              <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
                                Will be issued
                              </Badge>
                            </HStack>
                          </Box>
                        )}
                      </>
                    )}
                  </VStack>
                </Box>
              </TabPanel>
              {/* Recordings Tab - partners record table sessions free */}
              <TabPanel px={0}>
                <Box
                  bg="linear-gradient(145deg, rgba(20,30,48,0.9) 0%, rgba(10,15,25,0.95) 100%)"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="red.800"
                  p={{ base: 4, md: 6 }}
                >
                  <Text color="white" fontWeight="bold" fontSize="lg" mb={1}>Table recordings</Text>
                  <Text color="gray.400" fontSize="sm" mb={4}>
                    Open any table and tap Record. Your sessions land here to download and share. Partners record free.
                  </Text>
                  <RecordingsPanel compact />
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>

          {/* Tips Section */}
          <Box
            bg="linear-gradient(135deg, rgba(0,217,217,0.1) 0%, rgba(0,136,170,0.05) 100%)"
            borderRadius="xl"
            border="1px solid"
            borderColor="cyan.900"
            p={6}
          >
            <HStack mb={4}>
              <Text fontSize="lg"></Text>

              <Text color="white" fontWeight="bold">Tips to Maximize Your Earnings</Text>
            </HStack>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <HStack align="start" spacing={3}>
                <Box mt={1}>
                  <ChevronRight size={16} color={POKER_COLORS.cyan} />
                </Box>
                <Text color="gray.400" fontSize="sm">
                  Share your referral code on social media and poker forums
                </Text>
              </HStack>
              <HStack align="start" spacing={3}>
                <Box mt={1}>
                  <ChevronRight size={16} color={POKER_COLORS.cyan} />
                </Box>
                <Text color="gray.400" fontSize="sm">
                  Encourage players to participate in tournaments (10% rake!)
                </Text>
              </HStack>
              <HStack align="start" spacing={3}>
                <Box mt={1}>
                  <ChevronRight size={16} color="#9F7AEA" />
                </Box>
                <Text color="gray.400" fontSize="sm">
                  Share your bypass codes so players can create voice/video tables for free
                </Text>
              </HStack>
              <HStack align="start" spacing={3}>
                <Box mt={1}>
                  <ChevronRight size={16} color={POKER_COLORS.cyan} />
                </Box>
                <Text color="gray.400" fontSize="sm">
                  Contact support to upgrade your tier for higher rake share
                </Text>
              </HStack>
            </SimpleGrid>
          </Box>

          {/* Tier Info */}
          <Box
            bg="linear-gradient(145deg, rgba(20,30,48,0.9) 0%, rgba(10,15,25,0.95) 100%)"
            borderRadius="xl"
            border="1px solid"
            borderColor="whiteAlpha.100"
            p={6}
          >
            <HStack mb={4}>
              <Trophy size={20} color={POKER_COLORS.gold} />
              <Text color="white" fontWeight="bold">Agent Tiers</Text>
            </HStack>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Box
                p={4}
                bg={agent.tier === 'standard' ? `${POKER_COLORS.cyan}20` : 'blackAlpha.300'}
                borderRadius="lg"
                border="1px solid"
                borderColor={agent.tier === 'standard' ? POKER_COLORS.cyan : 'whiteAlpha.100'}
              >
                <HStack justify="space-between" mb={2}>
                  <Badge colorScheme="cyan">Standard</Badge>
                  {agent.tier === 'standard' && (
                    <Badge colorScheme="green">Current</Badge>
                  )}
                </HStack>
                <Text color="white" fontSize="2xl" fontWeight="bold">10%</Text>
                <Text color="gray.500" fontSize="sm">of rake generated</Text>
              </Box>
              <Box
                p={4}
                bg={agent.tier === 'premium' ? 'purple.900' : 'blackAlpha.300'}
                borderRadius="lg"
                border="1px solid"
                borderColor={agent.tier === 'premium' ? 'purple.500' : 'whiteAlpha.100'}
              >
                <HStack justify="space-between" mb={2}>
                  <Badge colorScheme="purple">Premium</Badge>
                  {agent.tier === 'premium' && (
                    <Badge colorScheme="green">Current</Badge>
                  )}
                </HStack>
                <Text color="white" fontSize="2xl" fontWeight="bold">15%</Text>
                <Text color="gray.500" fontSize="sm">of rake generated</Text>
              </Box>
              <Box
                p={4}
                bg={agent.tier === 'vip' ? 'yellow.900' : 'blackAlpha.300'}
                borderRadius="lg"
                border="1px solid"
                borderColor={agent.tier === 'vip' ? 'yellow.500' : 'whiteAlpha.100'}
              >
                <HStack justify="space-between" mb={2}>
                  <Badge colorScheme="yellow">VIP</Badge>
                  {agent.tier === 'vip' && (
                    <Badge colorScheme="green">Current</Badge>
                  )}
                </HStack>
                <Text color="white" fontSize="2xl" fontWeight="bold">20%</Text>
                <Text color="gray.500" fontSize="sm">of rake generated</Text>
              </Box>
            </SimpleGrid>
          </Box>
        </VStack>
      </Container>

      {/*  Payout Request Modal */}

      <Modal isOpen={isPayoutOpen} onClose={onPayoutClose} size="md">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="#0D1B2A" borderColor="green.500" border="2px">
          <ModalHeader color="green.400">
            <HStack>
              <Send size={24} />
              <Text>Request Payout</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="green.400" />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box p={4} bg="green.900" borderRadius="lg">
                <VStack align="start" spacing={1}>
                  <Text color="green.300" fontWeight="bold">Available Rake Earnings</Text>
                  <Text color={POKER_COLORS.gold} fontSize="2xl" fontWeight="bold">
                    {formatCurrency(payoutStatus?.availableAmount || 0)}
                  </Text>
                </VStack>
              </Box>

              <FormControl>
                <FormLabel color="gray.300">Payout Amount</FormLabel>
                <Input
                  type="number"
                  placeholder="Enter amount (min $10)"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  bg="blackAlpha.400"
                  borderColor="green.500"
                  color="white"
                />
              </FormControl>

              <FormControl>
                <FormLabel color="gray.300">Send it to</FormLabel>
                <HStack spacing={2}>
                  <Button size="sm" flex={1} variant={agentPayoutMethod === 'paypal' ? 'solid' : 'outline'} colorScheme="green" onClick={() => setAgentPayoutMethod('paypal')}>PayPal</Button>
                  <Button size="sm" flex={1} variant={agentPayoutMethod === 'usdc' ? 'solid' : 'outline'} colorScheme="green" isDisabled={!usdcPayoutsAvailable} onClick={() => setAgentPayoutMethod('usdc')}>USDC</Button>
                </HStack>
                {payoutDests[agentPayoutMethod]?.value ? (
                  <Text color="gray.400" fontSize="xs" mt={2}>
                    {agentPayoutMethod === 'usdc' ? 'Wallet' : 'PayPal'}: <Text as="span" color="white">{payoutDests[agentPayoutMethod].value}</Text>
                    {agentPayoutMethod === 'usdc' && payoutDests.usdc?.holdUntil ? ` (new address, ready ${new Date(payoutDests.usdc.holdUntil).toLocaleString()})` : ''}
                  </Text>
                ) : (
                  <HStack mt={2} justify="space-between">
                    <Text color="orange.300" fontSize="xs">No {agentPayoutMethod === 'usdc' ? 'USDC wallet' : 'PayPal contact'} saved yet.</Text>
                    <Button size="xs" variant="link" colorScheme="cyan" onClick={() => { onPayoutClose(); navigate('/wallet'); }}>Set it up in Wallet</Button>
                  </HStack>
                )}
              </FormControl>

              <Box p={3} bg="blackAlpha.400" borderRadius="md">
                <VStack align="start" spacing={1}>
                  <Text color="gray.300" fontSize="sm" fontWeight="bold">How agent payouts work</Text>
                  <Text color="gray.400" fontSize="sm">1. Verify your identity once (Wallet, Verify ID). Required before any payout.</Text>
                  <Text color="gray.400" fontSize="sm">2. Save your PayPal email or phone, or a USDC wallet address, under Wallet, Redeem. Agent payouts go to the same place as redemptions.</Text>
                  <Text color="gray.400" fontSize="sm">3. Request here. Minimum $10. Our team reviews it and sends PayPal by hand or USDC on-chain, usually within 1 to 3 business days.</Text>
                  <Text color="gray.400" fontSize="sm">4. The amount is held from your rake balance while the request is open and returned if it is declined.</Text>
                </VStack>
              </Box>

              {payoutStatus?.lastPayoutDate && (
                <Text color="orange.300" fontSize="sm">
                  Last payout: {payoutStatus.lastPayoutDate.toLocaleDateString()}
                </Text>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onPayoutClose} color="gray.400">Cancel</Button>
            <Button
              colorScheme="green"
              onClick={handleRequestPayout}
              isLoading={requestingPayout}
              leftIcon={<Send size={16} />}
              isDisabled={!payoutAmount || parseFloat(payoutAmount) < 10 || parseFloat(payoutAmount) > (payoutStatus?.availableAmount || 0) || !payoutDests[agentPayoutMethod]?.value}
            >
              Request Payout
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/*  NEW: Create Bypass Code Modal */}

      <Modal isOpen={isBypassModalOpen} onClose={onBypassModalClose} size="md">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="#0D1B2A" borderColor="purple.500" border="2px">
          <ModalHeader color="purple.400">
            <HStack>
              <Key size={24} />
              <Text>Create Bypass Code</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="purple.400" />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box p={4} bg="purple.900" borderRadius="lg">
                <HStack spacing={3}>
                  <Mic size={24} color="#9F7AEA" />
                  <Video size={24} color="#9F7AEA" />
                  <VStack align="start" spacing={0}>
                    <Text color="purple.300" fontWeight="bold">Voice & Video Chat Bypass</Text>
                    <Text color="gray.400" fontSize="sm">This code waives the $0.50 fee when creating tables with voice/video</Text>
                  </VStack>
                </HStack>
              </Box>

              <FormControl>
                <FormLabel color="gray.300">Description (optional)</FormLabel>
                <Input
                  placeholder="e.g., For my Discord community..."
                  value={bypassCodeDescription}
                  onChange={(e) => setBypassCodeDescription(e.target.value)}
                  bg="blackAlpha.400"
                  borderColor="purple.500"
                  color="white"
                />
                <Text color="gray.500" fontSize="xs" mt={1}>
                  Help you remember what this code is for
                </Text>
              </FormControl>

              <Box p={3} bg="blackAlpha.400" borderRadius="md">
                <VStack align="start" spacing={1}>
                  <Text color="gray.400" fontSize="sm">• Code will be auto-generated (8 characters)</Text>
                  <Text color="gray.400" fontSize="sm">• Unlimited uses per code</Text>
                  <Text color="gray.400" fontSize="sm">• Can be deactivated anytime</Text>
                  <Text color="gray.400" fontSize="sm">• Usage is tracked for your stats</Text>
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onBypassModalClose} color="gray.400">Cancel</Button>
            <Button
              colorScheme="purple"
              onClick={handleCreateBypassCode}
              isLoading={creatingBypassCode}
              leftIcon={<Plus size={16} />}
            >
              Generate Code
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AgentDashboard;